import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { take } from 'rxjs';

import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

import { CHAT_NAVIGATION_HANDLERS } from '@lib/chat/core/tokens/chat-navigation.tokens';
import { ChatMessage } from '@lib/chat/models/chat.interface';
import { ChatCommunicationService } from '@lib/chat/services/chat-communication.service';

import { CreateAppointmentModalType } from '@lib/core/models/create-appointment.interface';

// TODO: create message state handler with corresponding icons
enum MessageState {
  Pending = 'pending',
  Sent = 'sent',
  Failed = 'failed',
  Read = 'read',
}

@Component({
  selector: 'chat-message',
  standalone: true,
  imports: [NgClass, DatePipe, MatIcon, MatMenu, MatMenuItem, MatMenuTrigger],
  templateUrl: './chat-message.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageComponent {
  private readonly chatCommunicationService = inject(ChatCommunicationService);
  private readonly navigationHandlers = inject(CHAT_NAVIGATION_HANDLERS);

  protected readonly MessageState = MessageState;

  readonly index = input.required<number>();
  readonly ownUserId = input.required<string>();
  readonly threadUserId = input.required<string>();
  readonly message = input.required<ChatMessage>();

  protected readonly isMyMessage = computed<boolean>(() => {
    return this.message().userId === '00000000-0000-0000-0000-000000000000' || this.message().userId === this.ownUserId() || this.message().metadata?.['isLilo'] === 'true';
  });

  getUserReadReceipt() {
    const userReadReceipt = Object.entries(this.message().readReceipts).find(entry => entry[0] == this.threadUserId());

    return {
      readTime: userReadReceipt ? userReadReceipt[1] : null,
      isRead: !!userReadReceipt,
    };
  }

  getState() {
    const userReadReceipt = this.getUserReadReceipt();
    // TODO: create message +state handler with corresponding icons
    if (userReadReceipt.isRead) return MessageState.Read;
    else return MessageState.Sent;
  }

  getFiles() {
    return Object.entries(this.message().media)
      .filter(([key]) => !!this.message().media[key])
      .map(([key]) => key);
  }

  downloadFile(file: string) {
    this.chatCommunicationService
      .getFileContents(this.message().media[file])
      .pipe(take(1))
      .subscribe(data => {
        const url = window.URL.createObjectURL(data);
        window.open(url);
      });
  }

  isUpdated() {
    return Math.abs(this.message().updatedAt.getTime() - this.message().createdAt.getTime()) > 1000;
  }

  navigateToPrescriptionsTab(prescriptionId: string) {
    this.navigationHandlers.switchToPrescriptionTab(prescriptionId);
  }

  navigateToPrescriptionsPage(prescriptionId: string) {
    this.navigationHandlers.navigateToPrescription(prescriptionId, this.threadUserId());
  }

  openCreateManualAppointmentModal() {
    const metadata = this.message().metadata;

    if (metadata?.['special_message_type'] === 'appointment_request' && metadata?.['prescriptionId'] && metadata?.['partnerId'] && metadata?.['locationId']) {
      this.navigationHandlers.openCreateManualAppointmentModal?.(this.threadUserId(), CreateAppointmentModalType.ManualCreation, {
        prescriptionId: metadata['prescriptionId'],
        prescriptionName: metadata['prescriptionName'],
        partnerId: metadata['partnerId'],
        locationId: metadata['locationId'],
      });
    } else this.navigationHandlers.openCreateManualAppointmentModal?.(this.threadUserId(), CreateAppointmentModalType.ManualCreation);
  }
}
